// src/ui/features/calculator/utils/calculatorUtils.js

export function titleCaseDegree(degreeKey) {
  const map = {
    bachelors: "Bachelor’s",
    masters: "Master’s",
    integrated: "Integrated Master’s",
    phd: "PhD",
  };
  return map[degreeKey] || degreeKey;
}

export function roundDecimal(value, places = 2) {
  const p = Math.pow(10, places);
  return Math.round(value * p) / p;
}

export function normalizeNumber(v, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

export function csvEscape(value) {
  const s = String(value ?? "");
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

export function downloadCsv(filename, csvText) {
  // UTF-8 BOM so Excel parses correctly
  const BOM = "\uFEFF";
  const blob = new Blob([BOM + csvText], { type: "text/csv;charset=utf-8;" });

  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;

  document.body.appendChild(a);
  a.click();
  a.remove();

  URL.revokeObjectURL(url);
}

/**
 * Convert UI modulesByLevel -> engine inputs.
 * Engine tests indicate shape: { l5: [[grade, credits], ...], l6: [[grade, credits], ...] }
 */
export function toEngineInputs(modulesByLevel) {
  const out = {};
  for (const [levelKey, mods] of Object.entries(modulesByLevel || {})) {
    out[levelKey] = (mods || [])
      .filter((m) => Number(m?.credits) > 0)
      .map((m) => [Number(m?.grade) || 0, Number(m?.credits) || 0]);
  }
  return out;
}

/**
 * Map engine result -> UI result shape used by this feature.
 */
export function mapEngineResultToUi(result) {
  const avgRaw =
    result?.average ??
    result?.numeric ??
    (typeof result?.final === "string" ? parseFloat(result.final) : result?.final) ??
    null;

  const average = avgRaw == null ? "—" : roundDecimal(Number(avgRaw), 2);

  const clsObj = result?.classification;

  const classificationName =
    (clsObj && typeof clsObj === "object" && typeof clsObj.name === "string" && clsObj.name) ||
    (typeof result?.grade === "string" && result.grade) ||
    (typeof clsObj === "string" && clsObj) ||
    "—";

  const upgradedRaw =
    (clsObj && typeof clsObj === "object" ? clsObj.upgraded : null) ??
    (clsObj && typeof clsObj === "object" ? clsObj.upgrade : null) ??
    result?.upgraded ??
    result?.upgrade ??
    null;

  const upgraded =
    typeof upgradedRaw === "string"
      ? upgradedRaw.toLowerCase() === "yes"
      : Boolean(upgradedRaw);

  return {
    average,
    classification: {
      name: classificationName,
      status: upgraded ? "upgraded" : "clear",
      upgrade: upgraded ? "Yes" : null,
      upgraded,
      upgradeToKey:
        clsObj && typeof clsObj === "object"
          ? clsObj.upgradeToKey ?? null
          : result?.upgradeToKey ?? null,
      next: result?.next ?? null,
    },
    y2Avg: result?.y2Avg ?? null,
    y3Avg: result?.y3Avg ?? null,
  };
}

export function buildGradeBucketsFromModules(moduleStats) {
  const defs = [
    { label: "30–39", min: 30, max: 40 },
    { label: "40–49", min: 40, max: 50 },
    { label: "50–59", min: 50, max: 60 },
    { label: "60–69", min: 60, max: 70 },
    { label: "70–79", min: 70, max: 80 },
    { label: "80–89", min: 80, max: 90 },
    { label: "90+", min: 90, max: Infinity },
  ];

  const counts = defs.map((d) => ({ ...d, count: 0 }));

  const grades = (moduleStats?.modules || [])
    .map((m) => Number(m?.grade))
    .filter((g) => Number.isFinite(g));

  for (const g of grades) {
    const grade = Math.max(0, Math.min(100, g));
    const bucket = counts.find((b) => grade >= b.min && grade < b.max);
    if (bucket) bucket.count += 1;
  }

  return counts;
}
